import axios, { CancelTokenSource } from "axios";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Badge, Button, Icons, LoadingState, MessageState, RightRail, SidePanel, useToast } from "../../../../ds";
import { useResolvedPageFormat } from "../../../../astromark";
import { PageContentChangedEvent } from "../../../../common/events/PageContentChangedEvent";
import { useServiceCallPro } from "../../../../common/services/UseServiceCall";
import { IdentityService } from "../../../../lib/infrastructure/identity/IdentityService";
import { UISContext } from "../../../../lib/infrastructure/ui/UIServices";
import { LockPageDrawer } from "../../../locks/components/LockPageDrawer";
import { LockPanel } from "../../../locks/components/LockPanel";
import { LoadError, LoadGate } from "../../../locks/components/LockStates";
import { LockMarks } from "../../../locks/models/LockDTO";
import { lockErrorKind } from "../../../locks/services/LockService";
import { usePageMetadata } from "../../hooks/usePageMetadata";
import { Page } from "../../models/Page";
import { GetPageResponse } from "../../models/services/GetPageResponse";
import { PageFilesService } from "../../services/PageFilesService";
import { PageService } from "../../services/PageService";
import { Comments } from "./comments/Comments";
import { DropZonePopup, useFileDrop } from "./dropzone/DropZonePopup";
import { PageHistoryPopup } from "./history/PageHistoryPopup";
import { MediaPopup } from "./media/MediaPopup";
import { PageContentRenderer, usePreparedPage } from "./PageContentRenderer";
import { PageHeader, PageTrailItem } from "./PageHeader";
import { PageToc } from "./reader/PageToc";
import { countToc } from "./reader/readerBody";

export const EmptySpaceText = "Looks like this is empty! Try add a new page to this space!";
export const LockedYourselfOutText = "You are not one of its readers yet, so it is hidden from you until you are added in Identity Web.";

interface IPageContentProps {
    defaultPage?: number;
    onSave: () => void;
    /** Lock badges of the space's pages (from the manifest). */
    lockMarks?: LockMarks;
    /** The open page's ancestors, root first (the trail above the title). */
    ancestors?: { pageId: number; title: string }[];
    /** Opens the editor on a page (the layer lives with the tree, which edits pages too). */
    onEditPage: (pageId: string) => void;
    /** "New page" of the empty space. */
    onAddPage?: () => void;
    /** The page title shown, for the tab title ("<Page> | <Space> | Ark"). */
    onTitle?: (title: string | undefined) => void;
    /** Phones: one column; the panels fold into the page (the shell puts the tree in a drawer). */
    isPhone?: boolean;
    /** A layer of the tree's is open (editor, delete confirm): file drops are not for the page then. */
    suspendDrop?: boolean;
}

const pageSvc = new PageService();
const pageFilesSvc = new PageFilesService();

const mapGetPageResponseToPage = (page: GetPageResponse): Page => ({
    pageId: page.pageId,
    title: page.title,
    pageContent: page.pageContent,
    dateCreated: page.dateCreated,
    dateModified: page.dateModified,
    modifiedBy: page.modifiedBy,
});

/** The space's title from the shell's context (set by SpacesModule); undefined outside a space or before it loads. */
export function useSpaceTitle(): string | undefined {
    return useContext(UISContext)?.selectedSpace?.name;
}

/** A disclosure card for phones (the side panels fold into the page). */
function PhoneSection(props: { title: string; meta?: ReactNode; children: ReactNode; open?: boolean; onToggle?: (open: boolean) => void }) {
    return (
        <details className="ark-phone-section" open={props.open} onToggle={(ev) => props.onToggle?.((ev.currentTarget as HTMLDetailsElement).open)}>
            <summary>
                <span className="ark-phone-section-title">{props.title}</span>
                {props.meta ? <span className="ark-phone-section-meta">{props.meta}</span> : null}
                <Icons.chevronDown size={16} />
            </summary>
            <div className="ark-phone-section-body">{props.children}</div>
        </details>
    );
}

/**
 * The open page (board "Ark-Page"): header (*Lock page* while the page is unlocked), the lock card of a locked page and the body in the 1000 px reading column; the "On this page" and
 * Comments side panels and the right rail of toggles (TOC, Comments, Media); file drop anywhere; page history. Phones: one
 * column, a bar with Pages / comments / media, the TOC and the comments folded into cards.
 */
export function PageContent(props: IPageContentProps) {
    const cancelationToken = useRef<CancelTokenSource | undefined>(undefined);
    const toast = useToast();
    const navigate = useNavigate();
    const { spaceId, pageId } = useParams();
    const { onTitle, isPhone } = props;

    const [isCommentSectionOpen, setIsCommentSectionOpen] = useState(false);
    const [isTocSectionOpen, setIsTocSectionOpen] = useState(false);
    const [isMediaOpen, setIsMediaOpen] = useState(false);
    const [isDropOpen, setIsDropOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isLockOpen, setIsLockOpen] = useState(false);
    const [commentCount, setCommentCount] = useState<number>();
    const [pageInfo, setPageInfo] = useState<Page>();
    const [pageLoadError, setPageLoadError] = useState<LoadError>();

    const pageMetadata = usePageMetadata(pageId, pageInfo?.dateModified);
    // dev ?format= > the shell's render-mode switch > the page's metadata > the default (src/astromark)
    const format = useResolvedPageFormat(pageMetadata);
    const body = usePreparedPage(pageInfo?.pageContent, format);

    const lockMark = pageId ? props.lockMarks?.[Number(pageId)] : undefined;
    const currentUserId = useMemo(() => {
        try {
            return IdentityService.Global?.getClaims()?.oid as string | undefined;
        } catch {
            return undefined;
        }
    }, []);

    const getPageByIdCall = useServiceCallPro(pageSvc.getPage);
    const uploadFilesCall = useServiceCallPro(pageFilesSvc.uploadFiles);

    const spaceTitle = useSpaceTitle();
    const currentSpace = spaceTitle ?? spaceId;

    /****************************
     * DATA REQUESTS
     *****************************/

    const getPage = useCallback(() => {
        if (!pageId || !spaceId) return;

        if (cancelationToken.current) {
            cancelationToken.current.cancel();
        }
        cancelationToken.current = axios.CancelToken.source();

        setPageLoadError(undefined);
        getPageByIdCall
            .invoke(spaceId, pageId, cancelationToken.current.token)
            .then((response) => {
                setPageInfo(mapGetPageResponseToPage(response));
            })
            .catch((error) => {
                if (axios.isCancel(error)) return;
                // 404 = hidden (locked) or gone, the same answer for both; 503 = Identity unavailable (a page under a lock while lock grants are unknown)
                const kind = lockErrorKind(error);
                setPageInfo(undefined);
                setPageLoadError(kind === "not-found" || kind === "identity-unavailable" ? kind : undefined);
            });
    }, [getPageByIdCall, pageId, spaceId]);

    const handleLockHidden = useCallback(() => {
        toast.show({ tone: "info", title: "Page locked", text: LockedYourselfOutText });
        if (spaceId) navigate(`/spaces/${spaceId}/pages`, { replace: true });
    }, [toast, navigate, spaceId]);

    const uploadFiles = useCallback(
        (files: File[]) => {
            if (spaceId === undefined || pageId === undefined) return;
            uploadFilesCall
                .invoke(spaceId, pageId, files)
                .then((uploaded) => {
                    navigator.clipboard?.writeText(`![${uploaded[0].name}](${uploaded[0].url})`).catch(() => undefined);
                    toast.show({ tone: "success", title: "Success!", text: "Success uploading file!" });
                })
                .catch(() => {
                    toast.show({ tone: "danger", title: "Error!", text: "Error uploading file!" });
                })
                .finally(() => setIsDropOpen(false));
        },
        [uploadFilesCall.invoke, spaceId, pageId, toast]
    );

    /****************************
     * EFFECTS
     *****************************/

    useEffect(() => {
        setPageInfo(undefined);
        setCommentCount(undefined);
        getPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageId, spaceId]);

    useEffect(() => {
        const handlePageChanged = () => getPage();
        PageContentChangedEvent.attachHandler(handlePageChanged);
        return () => PageContentChangedEvent.detachHandler(handlePageChanged);
    }, [getPage]);

    useEffect(() => {
        if (!pageId && props.defaultPage) {
            navigate(props.defaultPage.toString(), { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageId, props.defaultPage]);

    useEffect(() => {
        onTitle?.(pageInfo?.title);
    }, [onTitle, pageInfo?.title]);

    const layerOpen = isHistoryOpen || isMediaOpen || isLockOpen || !!props.suspendDrop;
    useFileDrop({
        enabled: !!pageInfo && !layerOpen,
        onEnter: () => setIsDropOpen(true),
        onDrop: uploadFiles,
    });

    /****************************
     * USER ACTIONS
     *****************************/

    const handleEditPageClicked = useCallback(() => {
        if (pageId) props.onEditPage(pageId);
    }, [pageId, props]);

    const trail = useMemo((): PageTrailItem[] => {
        if (!spaceId) return [];
        const items: PageTrailItem[] = [];
        if (currentSpace) items.push({ text: currentSpace, to: `/spaces/${spaceId}/pages` });
        for (const ancestor of props.ancestors ?? []) items.push({ text: ancestor.title, to: `/spaces/${spaceId}/pages/${ancestor.pageId}` });
        return items;
    }, [spaceId, currentSpace, props.ancestors]);

    /****************************
     * HTML
     *****************************/

    // a reload after a save keeps the page on screen; only a page that is not there yet shows the loader
    const isWaiting = !!pageId && !pageInfo && !pageLoadError;

    if (pageLoadError) {
        return (
            <div className="ark-reader-main">
                <LoadGate error={pageLoadError} onRetry={getPage}>{null}</LoadGate>
            </div>
        );
    }

    if (isWaiting) {
        return (
            <div className="ark-reader-main">
                <LoadingState text="Loading the page…" />
            </div>
        );
    }

    if (!pageId || !pageInfo || !spaceId) {
        return (
            <div className="ark-reader-main">
                <MessageState
                    kind="add"
                    title={EmptySpaceText}
                    actionLabel={props.onAddPage ? "New page" : undefined}
                    actionIcon={<Icons.plus size={16} />}
                    onAction={props.onAddPage}
                />
            </div>
        );
    }

    const tocCount = countToc(body.toc);
    const actions = (
        <>
            {isPhone ? <Button type="primary" icon={<Icons.edit size={16} />} text="Edit Page" isFullWidth onClick={handleEditPageClicked} /> : null}
            <Button type="secondary" icon={<Icons.history size={16} />} text="See history" onClick={() => setIsHistoryOpen(true)} />
            {!lockMark ? <Button type="secondary" icon={<Icons.lock size={16} />} text="Lock page" onClick={() => setIsLockOpen(true)} /> : null}
            {isPhone ? <Button type="secondary" icon={<Icons.image size={16} />} text="Media" onClick={() => setIsMediaOpen(true)} /> : null}
            {!isPhone ? <Button type="primary" icon={<Icons.edit size={16} />} text="Edit Page" onClick={handleEditPageClicked} /> : null}
        </>
    );

    const toggles = [
        { icon: <Icons.toc size={18} />, label: "On this page", active: isTocSectionOpen, onToggle: () => setIsTocSectionOpen((v) => !v) },
        { icon: <Icons.comment size={18} />, label: "Comments", active: isCommentSectionOpen, onToggle: () => setIsCommentSectionOpen((v) => !v), badge: commentCount },
        { icon: <Icons.image size={18} />, label: "Media", active: isMediaOpen, onToggle: () => setIsMediaOpen((v) => !v) },
    ];

    return (
        <>
            <div className="ark-reader-main">
                <article className="ark-doc" aria-label={pageInfo.title}>
                    <PageHeader
                        title={pageInfo.title}
                        expedition={pageMetadata?.expedition}
                        tags={pageMetadata?.tags}
                        author={pageInfo.modifiedBy}
                        date={pageInfo.dateModified}
                        lock={lockMark}
                        trail={trail}
                    >
                        {actions}
                    </PageHeader>

                    <LockPanel
                        key={`${spaceId}-${pageId}-${lockMark ? "locked" : "open"}`}
                        spaceId={spaceId}
                        pageId={pageId}
                        currentUserId={currentUserId}
                        onChanged={props.onSave}
                        onHidden={handleLockHidden}
                    />

                    {isPhone && tocCount > 1 ? (
                        <PhoneSection title="On this page" meta={`${tocCount} sections`}>
                            <PageToc entries={body.toc} variant="side" />
                        </PhoneSection>
                    ) : null}

                    <PageContentRenderer
                        hideToc={isTocSectionOpen || !!isPhone}
                        markdownContent={pageInfo.pageContent}
                        format={format}
                        body={body}
                        emptyPageOnClick={handleEditPageClicked}
                    />

                    {isPhone ? (
                        <PhoneSection
                            title="Comments"
                            meta={commentCount ? <Badge count={commentCount} tone="neutral" size="sm" aria-label={`${commentCount} comments`} /> : undefined}
                        >
                            <Comments key={pageId} spaceId={spaceId} pageId={pageId} onCountChange={setCommentCount} />
                        </PhoneSection>
                    ) : null}
                </article>
            </div>

            {!isPhone && (isTocSectionOpen || isCommentSectionOpen) ? (
                <div className="ark-reader-panels">
                    <SidePanel title="On this page" open={isTocSectionOpen} onClose={() => setIsTocSectionOpen(false)}>
                        {tocCount > 0 ? <PageToc entries={body.toc} variant="side" /> : <p className="ark-muted">This page has no headings.</p>}
                    </SidePanel>
                    <SidePanel
                        title="Comments"
                        open={isCommentSectionOpen}
                        onClose={() => setIsCommentSectionOpen(false)}
                        actions={commentCount ? <Badge count={commentCount} tone="neutral" size="sm" aria-label={`${commentCount} comments`} /> : undefined}
                    >
                        <Comments key={pageId} spaceId={spaceId} pageId={pageId} onCountChange={setCommentCount} />
                    </SidePanel>
                </div>
            ) : null}

            {!isPhone ? <RightRail toggles={toggles} label="Page panels" /> : null}

            <LockPageDrawer
                open={isLockOpen}
                spaceId={spaceId}
                pageId={pageId}
                onClose={() => setIsLockOpen(false)}
                onLocked={() => {
                    setIsLockOpen(false);
                    props.onSave();
                }}
                onHidden={() => {
                    setIsLockOpen(false);
                    handleLockHidden();
                }}
            />
            <MediaPopup
                open={isMediaOpen}
                pageId={pageId}
                spaceId={spaceId}
                isUploading={uploadFilesCall.isLoading}
                handleFileUpload={uploadFiles}
                onClose={() => setIsMediaOpen(false)}
            />
            <DropZonePopup open={isDropOpen} isUploading={uploadFilesCall.isLoading} onClose={() => setIsDropOpen(false)} />
            {isHistoryOpen ? (
                <PageHistoryPopup
                    pageId={pageId}
                    spaceId={spaceId}
                    pageTitle={pageInfo.title}
                    lockMark={lockMark}
                    format={format}
                    onClose={() => setIsHistoryOpen(false)}
                />
            ) : null}
        </>
    );
}
